/**
 * Error markers in the session replay (ADR 0062).
 *
 * A Feedback ticket's confidence rises when the reporter's replay shows an
 * error near the moment they reported it. These markers record that an error
 * happened and where, never what: the API route is a template without IDs or
 * query, and no request or response body is kept.
 */

import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { recordRrwebMarker } from './RrwebRecorder';

export const API_ERROR_MARKER = 'rudi:api-error';
export const UI_ERROR_MARKER = 'rudi:ui-error';

export interface ApiErrorMarker {
  method: string;
  routeTemplate: string;
  /** HTTP status, or 0 when no response arrived. */
  status: number;
  requestId?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
const OPAQUE = /^[A-Za-z0-9_\-.~]{20,}$/;

function isIdentifier(segment: string): boolean {
  if (UUID.test(segment) || NUMERIC.test(segment)) return true;
  // An encoded or e-mail-like segment may carry a person's details.
  if (segment.includes('@') || segment.includes('%')) return true;
  return OPAQUE.test(segment) && /\d/.test(segment);
}

/**
 * The path of a URL with identifiers replaced by `:id` and no query or
 * fragment. Mirrors `route_template` in `rudi/domains/feedback/confidence.py`.
 */
export function routeTemplate(url: string): string {
  const raw = url.trim();
  let path: string;
  if (raw.includes('://')) {
    try {
      path = new URL(raw).pathname;
    } catch {
      path = '';
    }
  } else {
    path = raw.split('?')[0].split('#')[0];
  }
  const segments = path
    .split('/')
    .filter(Boolean)
    .map(segment => (isIdentifier(segment) ? ':id' : segment.toLowerCase()));
  return `/${segments.join('/')}`;
}

// Failures the recorder handles itself, and sign-in states that are not product errors.
const IGNORED_STATUSES = new Set([401, 403, 440]);

function isReplayUpload(url: string): boolean {
  return routeTemplate(url).startsWith('/cortex/replays');
}

/** The marker for a failed API call, or null when the failure is not one to record. */
export function apiErrorMarker(error: unknown): ApiErrorMarker | null {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return null;
  const failed = error as AxiosError;
  const url = failed.config?.url;
  if (!url || isReplayUpload(url)) return null;
  const status = failed.response?.status ?? 0;
  if (failed.response && (status < 400 || IGNORED_STATUSES.has(status))) return null;
  const marker: ApiErrorMarker = {
    method: (failed.config?.method ?? 'get').toUpperCase(),
    routeTemplate: routeTemplate(url),
    status,
  };
  const requestId = failed.response?.headers?.['x-request-id'];
  if (typeof requestId === 'string' && requestId) marker.requestId = requestId;
  return marker;
}

export function recordApiError(error: unknown): void {
  const marker = apiErrorMarker(error);
  if (marker) recordRrwebMarker(API_ERROR_MARKER, marker);
}

export function recordUiError(boundary: string): void {
  recordRrwebMarker(UI_ERROR_MARKER, { boundary });
}

const instrumented = new WeakSet<AxiosInstance>();

/** Adds the API error marker to a client once; the error still reaches the caller. */
export function installApiErrorMarkers(client: AxiosInstance): void {
  if (instrumented.has(client)) return;
  instrumented.add(client);
  client.interceptors.response.use(undefined, (error: unknown) => {
    recordApiError(error);
    return Promise.reject(error);
  });
}
